import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Hit, HitAttributeHighlightResult } from 'instantsearch.js';
import { algoliaClient } from '../client';
import { ALGOLIA_INDEX_MAIN, ALGOLIA_INDEX_QUERY_SUGGESTIONS } from '../variables';
import { sanitizeStringForUrl } from '~/features/navigation/components/search/search-utils';
import { createAbsoluteUrl } from '~/utils/url';

interface HookConfiguration {
    query: string;
}

export const useAlgoliaProductSearch = (config: HookConfiguration) => {
    const { query } = config;

    const { data, isFetching, dataUpdatedAt } = useQuery({
        queryFn: () => searchProductsWithAutoComplete(query),
        queryKey: ['useAlgoliaProductSearch', query],
        enabled: isValidSearchTerm(query),
        staleTime: 10 * 60 * 1000, // 10 minutes
        placeholderData: (prev) => prev,
    });

    const extractIndexData = useCallback(
        (index: string) => data?.find((result) => result.index === index),
        [dataUpdatedAt],
    );

    const autocomplete = useMemo(
        () => extractIndexData(ALGOLIA_INDEX_QUERY_SUGGESTIONS),
        [dataUpdatedAt],
    );

    const products = useMemo(() => extractIndexData(ALGOLIA_INDEX_MAIN), [dataUpdatedAt]);

    // We'll only utilize one query ID for both results.
    const queryId = autocomplete?.queryID;

    const generateSearchPath = useCallback(
        (value: string) => {
            let path = getDefaultSearchPath(value);

            // ASSERTION: We are confident that autocomplete.hits contains SearchSuggestion[] so, we can make this assertion.
            const exactMatch = getMatchingHit(autocomplete?.hits || [], value);

            if (exactMatch) path = getSearchHitPath(exactMatch);

            return path;
        },
        [dataUpdatedAt],
    );

    /**
     * OPTIONAL: We can validate our search path before navigating to it.
     * However, this has performance implications as page  fetches can take ~1s.
     */
    const generateValidSearchPath = useCallback(
        async (value: string) => {
            const defaultPath = getDefaultSearchPath(value);
            const targetPath = generateSearchPath(value);

            let path = defaultPath;

            /**
             * We will do a ping to the target path to see if it exists using HEAD since we don't need the expensive response body back.
             * If we do not get an "ok" from the response, we will use the default path.
             */
            if (defaultPath !== targetPath) {
                try {
                    const ping = await fetch(targetPath, { method: 'HEAD' });

                    if (ping.ok) path = targetPath;
                } catch (error) {
                    console.error(error);
                }
            }

            return path;
        },
        [generateSearchPath],
    );

    return {
        autocomplete,
        products,
        queryId,
        isFetching,
        dataUpdatedAt,

        generateSearchPath,
        generateValidSearchPath,
    };
};

export const isValidSearchTerm = (value: string) => !!value && value.length >= 3;

const searchProductsWithAutoComplete = async (query: string) => {
    try {
        const baseConfig = {
            query,
            clickAnalytics: true,

            // Used by <Highlight> to give emphasis to search term.
            highlightPreTag: '<mark>',
            highlightPostTag: '</mark>',
        };

        const request = await algoliaClient.search<Hit>([
            // Search Suggestions
            {
                indexName: ALGOLIA_INDEX_QUERY_SUGGESTIONS,
                params: {
                    ...baseConfig,
                    hitsPerPage: 6,
                },
            },
            // Product Suggestions
            {
                indexName: ALGOLIA_INDEX_MAIN,
                params: {
                    ...baseConfig,
                    hitsPerPage: 4,
                },
            },
        ]);

        return request.results;
    } catch (error) {
        console.error(error);

        throw error;
    }
};

const isHitAttributeHighlightResult = (value: unknown): value is HitAttributeHighlightResult =>
    !!value &&
    typeof (value as HitAttributeHighlightResult).matchLevel === 'string' &&
    typeof (value as HitAttributeHighlightResult).fullyHighlighted === 'boolean';

const isMatchingHit = (hit: Hit) => {
    const matchResult = hit._highlightResult?.query;

    if (isHitAttributeHighlightResult(matchResult)) {
        return matchResult?.matchLevel === 'full' && !!matchResult?.fullyHighlighted;
    }

    return false;
};

const getMatchingHit = (hits: Hit[], queryToMatch?: string) =>
    hits.find((hit) => {
        const matchesOptionalQuery =
            !queryToMatch ||
            (queryToMatch &&
                sanitizeStringForUrl(queryToMatch) === sanitizeStringForUrl(hit.query));

        return matchesOptionalQuery && isMatchingHit(hit);
    });

const getCategoriesFromHit = (hit: Hit): string[] => {
    const sanitizedQuery = sanitizeStringForUrl(hit.query);
    const facetMatches = hit[ALGOLIA_INDEX_MAIN]?.facets?.exact_matches;

    if (!facetMatches) return [];

    const categories = Object.entries(facetMatches).reduceRight((collection, entry) => {
        // Skip processing if we already have our collection.
        if (collection.length) return collection;

        const [key, list] = entry;

        if (!Array.isArray(list)) return collection;

        const value = list?.[0]?.value;

        // We will only process properties with "primary_category" and value of string.
        if (!key.startsWith('__primary_category') || typeof value !== 'string') return collection;

        const categoryList = value.split(' > ').map(sanitizeStringForUrl);
        const categoryIndex = categoryList.findIndex((category) => category === sanitizedQuery);

        // Exit if there are no matched categories.
        if (categoryIndex < 0) return collection;

        const applicableCategories = categoryList.splice(0, categoryIndex + 1);

        return applicableCategories;
    }, [] as string[]);

    return categories;
};

const generateCategoryPath = (categories: string[]) => {
    // Built path from extracted categories.
    const generatedPath = categories.reduce(
        (builtPath: string, category: string) => builtPath.concat(`/${category}`),
        '',
    );

    return generatedPath;
};

const getBrandFromHit = (hit: Hit): string | undefined => {
    const sanitizedQuery = sanitizeStringForUrl(hit.query);
    const facetMatches = hit[ALGOLIA_INDEX_MAIN]?.facets?.exact_matches;

    if (!facetMatches) return undefined;

    const sanitizedBrandName = sanitizeStringForUrl(facetMatches.brand?.[0].value);

    if (sanitizedBrandName === sanitizedQuery) {
        return sanitizedBrandName;
    }

    return undefined;
};

/**
 * TODO: Use base URL from Frame data.
 * Ex.: const brandsUrl = frame.settings?.pageReferences.brandsOverviewPage?.url || '/brands';
 */
const generateBrandPath = (brand: string, baseUrl: string = '/brands') => `${baseUrl}/${brand}`;

/**
 * TODO: Use base URL from Frame data.
 * Ex.: const searchUrl = frame.settings?.pageReferences.searchPage?.url || '/search';
 */
export const getDefaultSearchPath = (query: string, baseUrl: string = '/Søgeresultater') =>
    `${baseUrl}?query=${encodeURIComponent(query)}`;

export const getSearchHitPath = (hit: Hit) => {
    const defaultPath = getDefaultSearchPath(hit.query);

    // TODO: Move to SearchSUggestionsResults.tsx for memoization, if possible.
    const categories = getCategoriesFromHit(hit);
    const brand = getBrandFromHit(hit);

    // If we have matches for both, we will do the default behaviour.
    if (categories.length && brand) return defaultPath;

    if (categories.length) return generateCategoryPath(categories);
    if (brand) return generateBrandPath(brand);

    return defaultPath;
};

export const getSearchHitUrl = (hit: Hit) => {
    const path = getSearchHitPath(hit);
    const targetUrl = createAbsoluteUrl(path);

    return targetUrl.toString();
};
